import { RotateCcw } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { SurveyQuestionModal } from '../components/SurveyQuestionModal';
import { surveyQuestionsApi } from '../surveyApi';
import type { SurveyQuestion } from '../surveyTypes';

/**
 * The survey questionnaire — what the personnel profile's Survey panel is answered against.
 *
 * The standard management flow: index, create, profile, edit, delete. The anchor opens the
 * **profile**, not the edit form — every index page in this product is partnered with one, and
 * here the profile is where the count of answers already given is worth reading before
 * rewording a question, retiring it, or taking a follow-up away.
 */
export function SurveyQuestionsIndexPage() {
  const navigate = useNavigate();

  const [editing, setEditing] = useState<SurveyQuestion | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<{
    kind: 'deactivate' | 'activate';
    question: SurveyQuestion;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchQuestions = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      surveyQuestionsApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
        },
        signal,
      ),
    [],
  );

  const rowActions = useCallback(
    (question: SurveyQuestion): MenuItem[] => [
      {
        key: `edit-${question.id}`,
        label: 'Edit',
        permission: 'survey-questions.update',
        onClick: () => setEditing(question),
      },
      {
        key: `restore-${question.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'survey-questions.delete',
        disabledReason: question.isActive ? 'This question is already active.' : null,
        onClick: () => setConfirming({ kind: 'activate', question }),
      },
    ],
    [],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'deactivate') {
        await surveyQuestionsApi.deactivate(confirming.question.id);
      } else {
        await surveyQuestionsApi.activate(confirming.question.id);
      }

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0),
      );
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<SurveyQuestion>
        module="survey-questions"
        // Reached through Personnel → Settings, so Settings links: that crumb is the way back.
        breadcrumb={[
          'Human Resources',
          'Personnel',
          { label: 'Settings', to: '/personnel/settings' },
          'Survey',
        ]}
        columns={COLUMNS}
        facets={[{ key: 'status', label: 'Status' }]}
        auditEntityTypes={['SurveyQuestion']}
        rowKey={(question) => question.id}
        fetch={fetchQuestions}
        reloadKey={reloadKey}
        searchPlaceholder="Search questions"
        emptyMessage="No questions yet. Add the ones this employer asks — CS Form 212 prints items 34 to 40."
        onCreate={() => setCreating(true)}
        createPermission="survey-questions.create"
        // The anchor opens the profile; Edit is in the row menu. The read key, not the write
        // one — opening a record is not changing it.
        onRowOpen={(question) => navigate(`/personnel/settings/survey/${question.id}`)}
        openPermission="survey-questions.read"
        rowActions={rowActions}
        onDelete={(question) => setConfirming({ kind: 'deactivate', question })}
        deletePermission="survey-questions.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(question) =>
          question.isActive ? null : 'This question is already deleted.'
        }
        onClearData={() => surveyQuestionsApi.clear()}
        clearDataPermission="survey-questions.clear-data"
        otherInfo={(question) => question}
      />

      {creating || editing !== null ? (
        <SurveyQuestionModal
          open
          editing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'deactivate' ? 'Delete question' : 'Restore question'}
          danger={confirming.kind === 'deactivate'}
          // Both safeguards on the destructive half only. Restoring needs no ceremony.
          requireConfirm={confirming.kind === 'deactivate'}
          requireCredential={confirming.kind === 'deactivate'}
          loading={running}
          id={`survey-question-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `Item ${confirming.question.number} stops being asked on the survey.`
                  : `Item ${confirming.question.number} is asked again on the survey.`}
              </p>

              {confirming.kind === 'deactivate' && confirming.question.recordCount > 0 ? (
                <p>
                  {confirming.question.recordCount} employee
                  {confirming.question.recordCount === 1 ? ' has' : 's have'} already answered it.
                  Those answers stay on their records and still state the question — retiring it
                  does not erase what was said.
                </p>
              ) : null}

              <p>Nothing is erased — a deleted question can be restored from this list.</p>

              {error ? (
                <p
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700"
                >
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<SurveyQuestion>[] = [
  {
    // The item number is the anchor: "what did they say to 35b" is how this list is referred to.
    key: 'number',
    header: 'Item',
    width: 90,
    sortable: true,
    render: (question) => <TextCell value={question.number} mono />,
  },
  {
    key: 'text',
    header: 'Question',
    width: 420,
    flex: true,
    summary: true,
    render: (question) => <TextCell value={question.text} />,
  },
  {
    /*
      The five flags read down as a column of Asked/— rather than as a sentence per row: this
      list is scanned to compare questions against each other, which is what a grid is for.
      Em dash for "no", per the absent-value rule — a blank cell reads as a broken layout.
    */
    key: 'collectsDetails',
    header: 'Details',
    width: 90,
    render: (question) => <TextCell value={question.collectsDetails ? 'Asked' : null} />,
  },
  {
    key: 'collectsDate',
    header: 'Date filed',
    width: 100,
    render: (question) => <TextCell value={question.collectsDate ? 'Asked' : null} />,
  },
  {
    key: 'collectsCaseStatus',
    header: 'Case status',
    width: 110,
    render: (question) => <TextCell value={question.collectsCaseStatus ? 'Asked' : null} />,
  },
  {
    key: 'collectsIdNumber',
    header: 'ID no.',
    width: 90,
    render: (question) => <TextCell value={question.collectsIdNumber ? 'Asked' : null} />,
  },
  {
    key: 'collectsCountry',
    header: 'Country',
    width: 100,
    render: (question) => <TextCell value={question.collectsCountry ? 'Asked' : null} />,
  },
  {
    // The stored sort value, under a heading that says so: a POSITION is only correct with the
    // whole ladder in hand, and a paged table never has it.
    key: 'sortOrder',
    header: 'Order',
    width: 90,
    align: 'right',
    sortable: true,
    render: (question) => <TextCell value={String(question.sortOrder)} mono numeric />,
  },
  {
    key: 'recordCount',
    header: 'Answers',
    width: 100,
    align: 'right',
    summary: true,
    // The count is of personnel records, which is another module's data — the question and its
    // flags beside it are this module's own and stay ungated.
    permission: 'personnel.read',
    render: (question) => <TextCell value={String(question.recordCount)} mono numeric />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (question) =>
      question.isActive ? (
        <StatusText tone="active">Active</StatusText>
      ) : (
        <StatusText tone="inactive">Deleted</StatusText>
      ),
  },
];
